import React, { useEffect, useState } from 'react';
import { IonPage, IonContent } from '@ionic/react';
import { useParams } from 'react-router-dom';
import { getConsentByToken, submitConsentByToken, type ConsentFormData } from '@/api/consent';
import '@/theme/login.css';
import '@/theme/consent.css';

type PageState = 'loading' | 'form' | 'submitting' | 'success' | 'error';

const consentDescriptions: Record<string, string> = {
  data_processing:
    'Sunt de acord cu prelucrarea datelor personale (nume, adresă, telefon, email) în scopul evidenței membrilor bisericii.',
  photo_usage:
    'Sunt de acord cu utilizarea fotografiei mele în cadrul sistemului de gestiune al bisericii.',
  contact:
    'Sunt de acord să fiu contactat(ă) prin telefon, email sau mesaje pentru activitățile bisericii.',
  minor_data:
    'Sunt de acord cu prelucrarea datelor personale ale minorului aflat în grija mea.',
};

const ConsentPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<PageState>('loading');
  const [formData, setFormData] = useState<ConsentFormData | null>(null);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [errorMessage, setErrorMessage] = useState('');
  const [showValidation, setShowValidation] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getConsentByToken(token)
      .then((data) => {
        if (cancelled) return;
        setFormData(data);
        setState('form');
      })
      .catch((err) => {
        if (cancelled) return;
        const msg =
          err?.response?.data?.message ||
          'Acest link de consimțământ este invalid sau a expirat.';
        setErrorMessage(msg);
        setState('error');
      });
    return () => { cancelled = true; };
  }, [token]);

  const toggleType = (value: string) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(value)) {
        next.delete(value);
      } else {
        next.add(value);
      }
      return next;
    });
    setShowValidation(false);
  };

  const toggleAll = () => {
    if (!formData) return;
    const allValues = formData.consent_types.map((t) => t.value);
    if (checked.size === allValues.length) {
      setChecked(new Set());
    } else {
      setChecked(new Set(allValues));
    }
    setShowValidation(false);
  };

  const handleSubmit = async () => {
    if (checked.size === 0) {
      setShowValidation(true);
      return;
    }
    setState('submitting');
    try {
      await submitConsentByToken(token, Array.from(checked));
      setState('success');
    } catch (err: any) {
      const msg =
        err?.response?.data?.message || 'A apărut o eroare. Încercați din nou.';
      setErrorMessage(msg);
      setState('error');
    }
  };

  const formatDate = (iso: string) => {
    return new Date(iso).toLocaleDateString('ro-RO', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  return (
    <IonPage>
      <IonContent fullscreen scrollY>
        <div className="login-split">
          {/* Left panel — form */}
          <div className="login-form-panel">
            <div className="consent-form-inner">
              {state === 'loading' && (
                <div className="consent-loading">
                  <div className="consent-loading__spinner" />
                  <p className="consent-subtitle">Se încarcă formularul...</p>
                </div>
              )}

              {state === 'error' && (
                <div className="consent-error-state">
                  <div className="consent-error-state__icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="15" y1="9" x2="9" y2="15" />
                      <line x1="9" y1="9" x2="15" y2="15" />
                    </svg>
                  </div>
                  <h2 className="consent-error-state__title">Link invalid</h2>
                  <p className="consent-error-state__text">{errorMessage}</p>
                </div>
              )}

              {state === 'success' && (
                <div className="consent-success login-animate login-animate--1">
                  <div className="consent-success__icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                  <h2 className="consent-success__title">Consimțământ înregistrat</h2>
                  <p className="consent-success__text">
                    Vă mulțumim, {formData?.member_name}. Consimțământul dumneavoastră a fost
                    înregistrat cu succes. Puteți închide această pagină.
                  </p>
                </div>
              )}

              {(state === 'form' || state === 'submitting') && formData && (
                <>
                  <div className="consent-header login-animate login-animate--1">
                    <h1 className="consent-title">Consimțământ GDPR</h1>
                    <p className="consent-subtitle">
                      Bună ziua, <span className="consent-member-name">{formData.member_name}</span>.
                      Vă rugăm să confirmați consimțământul pentru prelucrarea datelor dumneavoastră personale.
                    </p>
                  </div>

                  <div className="consent-select-all login-animate login-animate--2">
                    <button type="button" onClick={toggleAll}>
                      {checked.size === formData.consent_types.length
                        ? 'Deselectează toate'
                        : 'Selectează toate'}
                    </button>
                  </div>

                  <div className="consent-types">
                    {formData.consent_types.map((type, i) => (
                      <div
                        key={type.value}
                        className={`consent-type-item login-animate login-animate--${i + 3}${
                          checked.has(type.value) ? ' consent-type-item--checked' : ''
                        }`}
                        onClick={() => toggleType(type.value)}
                        role="checkbox"
                        aria-checked={checked.has(type.value)}
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === ' ' || e.key === 'Enter') {
                            e.preventDefault();
                            toggleType(type.value);
                          }
                        }}
                      >
                        <div className="consent-type-item__checkbox">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        </div>
                        <div>
                          <div className="consent-type-item__label">{type.label}</div>
                          <div className="consent-type-item__desc">
                            {consentDescriptions[type.value] || ''}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {showValidation && (
                    <p className="consent-error-hint login-animate login-animate--1">
                      Selectați cel puțin un tip de consimțământ.
                    </p>
                  )}

                  <div className="consent-actions login-animate login-animate--7">
                    <button
                      type="button"
                      className="login-btn login-btn--primary"
                      onClick={handleSubmit}
                      disabled={state === 'submitting'}
                    >
                      {state === 'submitting' && <span className="login-spinner" />}
                      Confirm consimțământul
                    </button>
                  </div>

                  <div className="consent-expires login-animate login-animate--7">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                    Link valid până la {formatDate(formData.expires_at)}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Right panel — brand */}
          <div className="login-brand-panel">
            <div className="login-brand-panel__shapes">
              <div className="login-decor login-decor--1" />
              <div className="login-decor login-decor--2" />
              <div className="login-decor login-decor--3" />
              <div className="login-decor login-decor--4" />
              <div className="login-decor login-decor--5" />
            </div>
            <div className="login-brand-content">
              <div className="login-brand-logo">
                <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z" />
                </svg>
              </div>
              <h2 className="login-brand-name">Protecția datelor</h2>
              <p className="login-brand-tagline">
                Datele dumneavoastră sunt protejate conform Regulamentului General privind Protecția Datelor (GDPR).
              </p>
              <div className="login-brand-features">
                <div className="login-brand-feature">
                  <span className="login-brand-feature__dot" />
                  Datele sunt stocate în siguranță
                </div>
                <div className="login-brand-feature">
                  <span className="login-brand-feature__dot" />
                  Aveți dreptul de retragere oricând
                </div>
                <div className="login-brand-feature">
                  <span className="login-brand-feature__dot" />
                  Nu partajăm datele cu terți
                </div>
              </div>
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default ConsentPage;
